import { useMemo } from 'react';
import { Player } from '@remotion/player';
import {
  useCurrentFrame,
  useVideoConfig,
  spring,
  interpolate,
  AbsoluteFill,
} from 'remotion';
import { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

// Color theme configuration
const colorThemes = {
  cyan: {
    primary: '#06b6d4',
    secondary: '#22d3ee',
    gradient: 'from-cyan-500/5',
    text: 'text-cyan-500',
    bg: 'bg-cyan-500/10',
    glow: 'rgba(6, 182, 212, 0.6)',
  },
  green: {
    primary: '#10b981',
    secondary: '#34d399',
    gradient: 'from-emerald-500/5',
    text: 'text-emerald-500',
    bg: 'bg-emerald-500/10',
    glow: 'rgba(16, 185, 129, 0.6)',
  },
  orange: {
    primary: '#f59e0b',
    secondary: '#fbbf24',
    gradient: 'from-amber-500/5',
    text: 'text-amber-500',
    bg: 'bg-amber-500/10',
    glow: 'rgba(245, 158, 11, 0.6)',
  },
  blue: {
    primary: '#3b82f6',
    secondary: '#60a5fa',
    gradient: 'from-blue-500/5',
    text: 'text-blue-500',
    bg: 'bg-blue-500/10',
    glow: 'rgba(59, 130, 246, 0.6)',
  },
  purple: {
    primary: '#a855f7',
    secondary: '#c084fc',
    gradient: 'from-purple-500/5',
    text: 'text-purple-500',
    bg: 'bg-purple-500/10',
    glow: 'rgba(168, 85, 247, 0.6)',
  },
} as const;

export type ColorTheme = keyof typeof colorThemes;

export interface AnimatedMetricCardProps {
  title: string;
  subtitle?: string;
  icon: LucideIcon;
  mainValue: number;
  subLabel: string;
  progressPercentage: number;
  colorTheme: ColorTheme;
  format?: 'number' | 'currency' | 'percentage';
  secondaryMetrics?: Array<{
    label: string;
    value: number | string;
    color?: string;
  }>;
}

// Format value based on type
function formatValue(value: number, format: 'number' | 'currency' | 'percentage'): string {
  switch (format) {
    case 'currency':
      return new Intl.NumberFormat('he-IL', {
        style: 'currency',
        currency: 'ILS',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    case 'percentage':
      return `${Math.round(value)}%`;
    default:
      return Math.round(value).toLocaleString();
  }
}

// Inner composition props
interface CompositionProps {
  mainValue: number;
  progressPercentage: number;
  colorTheme: ColorTheme;
  format: 'number' | 'currency' | 'percentage';
}

// Animated SVG Progress Bar
function AnimatedProgressBar({
  progress,
  color,
  glow,
}: {
  progress: number;
  color: string;
  glow: string;
}) {
  const lineLength = 288;
  const dashOffset = lineLength - (progress / 100) * lineLength;
  const leadingX = 6 + (progress / 100) * lineLength;

  return (
    <svg
      width="100%"
      height="12"
      viewBox="0 0 300 12"
      preserveAspectRatio="none"
      style={{ overflow: 'visible' }}
    >
      <defs>
        {/* Glow filter for leading edge */}
        <filter id="progressGlow" x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        {/* Gradient for progress line */}
        <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={color} stopOpacity="0.4" />
          <stop offset="85%" stopColor={color} stopOpacity="1" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="1" />
        </linearGradient>
      </defs>

      {/* Background track */}
      <line
        x1="6"
        y1="6"
        x2="294"
        y2="6"
        stroke="rgba(100,116,139,0.2)"
        strokeWidth="4"
        strokeLinecap="round"
      />

      {/* Animated progress line */}
      <line
        x1="6"
        y1="6"
        x2="294"
        y2="6"
        stroke={color}
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray={lineLength}
        strokeDashoffset={dashOffset}
      />

      {/* Leading edge glow */}
      {progress > 0 && (
        <>
          {/* Outer glow */}
          <circle
            cx={leadingX}
            cy="6"
            r="6"
            fill={glow}
            filter="url(#progressGlow)"
            opacity="0.8"
          />
          {/* Inner bright dot */}
          <circle cx={leadingX} cy="6" r="3" fill="#ffffff" />
        </>
      )}
    </svg>
  );
}

// The Remotion composition component
function MetricCardComposition({
  mainValue,
  progressPercentage,
  colorTheme,
  format,
}: CompositionProps) {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const theme = colorThemes[colorTheme];

  // Animation completes in first 300 frames (~5 seconds), then holds for remaining 300 frames
  const animationFrame = Math.min(frame, 300);

  // Rolling number animation with spring physics - slower, smoother motion
  const numberProgress = spring({
    frame: animationFrame,
    fps,
    config: {
      damping: 80,
      stiffness: 40,
      mass: 1.2,
    },
  });

  const animatedValue = interpolate(numberProgress, [0, 1], [0, mainValue], {
    extrapolateRight: 'clamp',
  });

  // Progress bar animation (slightly delayed) - slower, smoother motion
  const progressSpring = spring({
    frame: Math.max(0, animationFrame - 20),
    fps,
    config: {
      damping: 60,
      stiffness: 30,
      mass: 1.5,
    },
  });

  const animatedProgress = interpolate(
    progressSpring,
    [0, 1],
    [0, progressPercentage],
    { extrapolateRight: 'clamp' }
  );

  // Scanline animation (continuous loop)
  const scanlineY = interpolate(frame % 120, [0, 120], [-20, 120], {
    extrapolateRight: 'clamp',
  });

  // Subtle pulse animation for the glow
  const pulseOpacity = interpolate(
    Math.sin((frame / fps) * Math.PI * 2),
    [-1, 1],
    [0.02, 0.05]
  );

  return (
    <AbsoluteFill
      style={{
        backgroundColor: 'transparent',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      {/* Scanline effect */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: `${scanlineY}%`,
          height: '40px',
          background: `linear-gradient(180deg, transparent 0%, ${theme.primary}15 50%, transparent 100%)`,
          pointerEvents: 'none',
          opacity: 0.6,
        }}
      />

      {/* Ambient glow pulse */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(ellipse at 30% 20%, ${theme.primary}, transparent 70%)`,
          opacity: pulseOpacity,
          pointerEvents: 'none',
        }}
      />

      {/* Main value display */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '12px',
          width: '100%',
          padding: '0 16px',
        }}
      >
        {/* Animated number */}
        <div
          style={{
            fontSize: '42px',
            fontWeight: 700,
            color: theme.primary,
            textShadow: `0 0 20px ${theme.glow}`,
            letterSpacing: '-0.02em',
          }}
        >
          {formatValue(animatedValue, format)}
        </div>

        {/* Progress bar */}
        <div style={{ width: '100%', maxWidth: '280px' }}>
          <AnimatedProgressBar
            progress={animatedProgress}
            color={theme.primary}
            glow={theme.glow}
          />
        </div>

        {/* Progress percentage */}
        <div
          style={{
            fontSize: '12px',
            color: 'rgba(148, 163, 184, 0.8)',
            fontWeight: 500,
          }}
        >
          {Math.round(animatedProgress)}% Complete
        </div>
      </div>
    </AbsoluteFill>
  );
}

// Main exported component
export function AnimatedMetricCard({
  title,
  subtitle,
  icon: Icon,
  mainValue,
  subLabel,
  progressPercentage,
  colorTheme,
  format = 'number',
  secondaryMetrics,
}: AnimatedMetricCardProps) {
  const theme = colorThemes[colorTheme];

  // Memoize input props to prevent unnecessary re-renders
  const inputProps = useMemo(
    () => ({
      mainValue,
      progressPercentage,
      colorTheme,
      format,
    }),
    [mainValue, progressPercentage, colorTheme, format]
  );

  return (
    <div
      className={cn(
        'group relative overflow-hidden rounded-xl sm:rounded-2xl',
        'bg-gradient-to-br from-slate-900/95 to-slate-800/90',
        'backdrop-blur-xl border border-white/10',
        'shadow-[0_8px_32px_rgba(0,0,0,0.3),0_2px_8px_rgba(0,0,0,0.2)]',
        'transition-all duration-500 ease-out',
        'hover:-translate-y-1 sm:hover:-translate-y-2',
        'hover:shadow-[0_20px_60px_rgba(0,0,0,0.4),0_8px_24px_rgba(0,0,0,0.3)]'
      )}
    >
      {/* Gradient overlay */}
      <div
        className={cn(
          'absolute inset-0 bg-gradient-to-br to-transparent pointer-events-none',
          theme.gradient
        )}
      />

      <div className="relative p-4 sm:p-5 lg:p-6">
        {/* Header */}
        <div className="flex items-center gap-2 mb-3 sm:mb-4 overflow-hidden">
          {/* Icon - fixed size */}
          <div
            className={cn(
              'flex-shrink-0 flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl',
              theme.bg,
              'backdrop-blur-sm',
              'transition-all duration-300',
              'group-hover:scale-110 group-hover:rotate-3'
            )}
          >
            <Icon className={cn('h-5 w-5 sm:h-6 sm:w-6', theme.text)} />
          </div>

          {/* Title - flexible, truncates */}
          <div className="flex-1 min-w-0">
            <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-white truncate">{title}</h3>
            {subtitle && (
              <p className="text-xs sm:text-sm text-slate-400 truncate">{subtitle}</p>
            )}
          </div>
        </div>

        {/* Remotion Player - Animated Content */}
        <div className="relative rounded-lg sm:rounded-xl overflow-hidden bg-slate-950/50 mb-3 sm:mb-4">
          <Player
            component={MetricCardComposition}
            inputProps={inputProps}
            durationInFrames={600}
            fps={60}
            compositionWidth={400}
            compositionHeight={200}
            style={{
              width: '100%',
              aspectRatio: '2/1',
            }}
            loop
            autoPlay
            controls={false}
          />
        </div>

        {/* Sub label */}
        <p className="text-xs sm:text-sm text-slate-400 text-center mb-3 sm:mb-4 truncate">{subLabel}</p>

        {/* Secondary metrics grid */}
        {secondaryMetrics && secondaryMetrics.length > 0 && (
          <div className="grid grid-cols-2 gap-2 sm:gap-3 pt-3 sm:pt-4 border-t border-white/10">
            {secondaryMetrics.map((metric, index) => (
              <div key={index} className="space-y-0.5 sm:space-y-1 min-w-0">
                <p className="text-[10px] sm:text-xs text-slate-500 uppercase tracking-wide truncate">
                  {metric.label}
                </p>
                <p
                  className={cn(
                    'text-base sm:text-lg font-semibold truncate',
                    metric.color || 'text-slate-200'
                  )}
                >
                  {metric.value}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Shine effect on hover */}
      <div
        className={cn(
          'absolute -top-24 -right-24 w-48 h-48 rounded-full',
          'bg-gradient-to-br to-transparent',
          'opacity-0 group-hover:opacity-100',
          'transition-opacity duration-700',
          'pointer-events-none blur-2xl'
        )}
        style={{
          background: `radial-gradient(circle, ${theme.primary}30 0%, transparent 70%)`,
        }}
      />
    </div>
  );
}

export default AnimatedMetricCard;
